import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { ShelfLifePreset, StorageSummary } from '@almox/shared';
import { api } from '../api/client.js';
import { useAppData } from '../AppData.js';
import { useAuth } from '../Auth.js';
import { STORAGE_ICON_OPTIONS, storageEmoji, useAsync, useToast } from '../components/ui.js';

/** Configuracoes: dispensas, validades padrao, alerta e exportacao. */
export function SettingsPage() {
  const { storages, settings, refresh } = useAppData();
  const { session, logout } = useAuth();
  const toast = useToast();
  const presets = useAsync(() => api.listPresets(), []);
  const [alertDays, setAlertDays] = useState<number | null>(null);

  async function run(action: () => Promise<unknown>, message?: string) {
    try {
      await action();
      await refresh();
      presets.reload();
      if (message) toast(message, 'ok');
    } catch (error) {
      toast((error as Error).message, 'error');
    }
  }

  return (
    <main className="page">
      <div className="page-head">
        <h1>Configuracoes</h1>
      </div>

      <section className="card">
        <h2>Alerta de vencimento</h2>
        <p className="small muted">
          Itens que vencem dentro desse prazo aparecem destacados na home e na dispensa.
        </p>
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div className="field" style={{ marginBottom: 0, maxWidth: 200 }}>
            <label htmlFor="alert-days">Dias de antecedencia</label>
            <input
              id="alert-days"
              type="number"
              min={0}
              max={90}
              value={alertDays ?? settings.expiryAlertDays}
              onChange={(event) => setAlertDays(Number(event.target.value))}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary"
            style={{ flex: '0 0 auto' }}
            onClick={() =>
              void run(
                () => api.updateSettings({ expiryAlertDays: alertDays ?? settings.expiryAlertDays }),
                'Alerta atualizado.',
              )
            }
          >
            Salvar
          </button>
        </div>
        <label className="check" style={{ marginTop: 12 }}>
          <input
            type="checkbox"
            checked={settings.externalCatalogEnabled}
            onChange={(event) =>
              void run(() => api.updateSettings({ externalCatalogEnabled: event.target.checked }))
            }
          />
          Consultar o catalogo publico (Open Food Facts) em codigos desconhecidos
        </label>
        <label className="check" style={{ marginTop: 8 }}>
          <input
            type="checkbox"
            checked={settings.photoAiEnabled}
            disabled={!settings.photoAiAvailable}
            onChange={(event) =>
              void run(() => api.updateSettings({ photoAiEnabled: event.target.checked }))
            }
          />
          Identificar produto novo pela foto da embalagem (IA de visao)
        </label>
        <p className="small muted" style={{ marginTop: 4 }}>
          {settings.photoAiAvailable
            ? 'Usada so quando voce tira a foto de um produto que ainda nao esta no catalogo. A foto vai para a OpenAI; nenhum outro dado do estoque e enviado.'
            : 'Indisponivel ate cadastrar a chave da OpenAI na area de administracao.'}
          {!settings.photoAiAvailable ? (
            <>
              {' '}
              <Link to="/admin">Abrir administracao</Link>
            </>
          ) : null}
        </p>
      </section>

      <StorageSettings storages={storages} onChange={run} />

      <section className="card">
        <h2>Validade padrao por categoria</h2>
        <p className="small muted">
          Usada para estimar a validade quando voce nao informa a data. A UI marca essas datas com
          “~”.
        </p>
        <PresetForm onSave={(category, days) => run(() => api.savePreset(category, days), 'Validade salva.')} />
        <table className="table">
          <thead>
            <tr>
              <th>Categoria</th>
              <th style={{ width: 120 }}>Dias</th>
              <th style={{ width: 100 }} />
            </tr>
          </thead>
          <tbody>
            {(presets.data ?? []).map((preset: ShelfLifePreset) => (
              <tr key={preset.id}>
                <td>{preset.category}</td>
                <td>
                  <input
                    type="number"
                    min={1}
                    defaultValue={preset.days}
                    style={{ width: 100 }}
                    onBlur={(event) => {
                      const days = Number(event.target.value);
                      if (days > 0 && days !== preset.days) {
                        void run(() => api.savePreset(preset.category, days));
                      }
                    }}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => void run(() => api.deletePreset(preset.id))}
                  >
                    Remover
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="card">
        <h2>Mais</h2>
        <p className="small muted">
          No celular estas telas ficam aqui — a barra de baixo guarda o uso do dia a dia.
        </p>
        <div className="row">
          <Link className="btn btn-block" to="/produtos">
            🏷 Cadastro de produtos
          </Link>
          <Link className="btn btn-block" to="/inventario">
            🧾 Conferir dispensa
          </Link>
          <Link className="btn btn-block" to="/sem-codigo">
            🥬 Entrada sem codigo
          </Link>
          <Link className="btn btn-block" to="/app">
            📱 App para Android
          </Link>
          <Link className="btn btn-block" to="/admin">
            🔒 Administracao
          </Link>
          <button
            type="button"
            className="btn btn-block"
            onClick={() => void logout()}
          >
            🚪 Sair ({session.username})
          </button>
        </div>
      </section>

      <section className="card">
        <h2>Seus dados</h2>
        <p className="small muted">
          Exportacao completa, sem lock-in: JSON para backup e CSV para planilha.
        </p>
        <div className="row">
          <a className="btn" href="/api/v1/export?format=json" download style={{ flex: '0 0 auto' }}>
            ⬇ Exportar JSON
          </a>
          <a className="btn" href="/api/v1/export?format=csv" download style={{ flex: '0 0 auto' }}>
            ⬇ Exportar CSV do estoque
          </a>
        </div>
      </section>
    </main>
  );
}

function StorageSettings({
  storages,
  onChange,
}: {
  storages: StorageSummary[];
  onChange: (action: () => Promise<unknown>, message?: string) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('box');
  const [color, setColor] = useState('#2f80ed');

  function create(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) return;
    const payload = { name: name.trim(), icon, color };
    setName('');
    void onChange(() => api.createStorage(payload), 'Dispensa criada.');
  }

  function move(index: number, direction: -1 | 1) {
    const next = [...storages];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    const [moved] = next.splice(index, 1);
    if (moved) next.splice(target, 0, moved);
    void onChange(() => api.reorderStorages(next.map((storage) => storage.id)));
  }

  return (
    <section className="card">
      <h2>Dispensas</h2>
      <form onSubmit={create} className="row" style={{ alignItems: 'flex-end' }}>
        <div className="field" style={{ marginBottom: 0, flex: '2 1 200px' }}>
          <label htmlFor="storage-name">Nome</label>
          <input
            id="storage-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Ex.: Armario do banheiro"
          />
        </div>
        <div className="field" style={{ marginBottom: 0, maxWidth: 130 }}>
          <label htmlFor="storage-icon">Icone</label>
          <select id="storage-icon" value={icon} onChange={(event) => setIcon(event.target.value)}>
            {STORAGE_ICON_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {storageEmoji(option)} {option}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0, maxWidth: 110 }}>
          <label htmlFor="storage-color">Cor</label>
          <input
            id="storage-color"
            type="color"
            value={color}
            onChange={(event) => setColor(event.target.value)}
          />
        </div>
        <button type="submit" className="btn btn-primary" style={{ flex: '0 0 auto' }}>
          Criar
        </button>
      </form>

      <table className="table" style={{ marginTop: 16 }}>
        <tbody>
          {storages.map((storage, index) => (
            <tr key={storage.id}>
              <td style={{ width: 40 }}>{storageEmoji(storage.icon)}</td>
              <td>
                <input
                  defaultValue={storage.name}
                  onBlur={(event) => {
                    const value = event.target.value.trim();
                    if (value && value !== storage.name) {
                      void onChange(() => api.updateStorage(storage.id, { name: value }));
                    }
                  }}
                />
              </td>
              <td style={{ width: 90 }}>
                <input
                  type="color"
                  defaultValue={storage.color}
                  onBlur={(event) =>
                    void onChange(() => api.updateStorage(storage.id, { color: event.target.value }))
                  }
                />
              </td>
              <td style={{ width: 60 }} className="small muted">
                {storage.itemCount} itens
              </td>
              <td style={{ width: 190 }}>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-sm btn-icon" onClick={() => move(index, -1)}>
                    ↑
                  </button>
                  <button type="button" className="btn btn-sm btn-icon" onClick={() => move(index, 1)}>
                    ↓
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => {
                      const message =
                        storage.itemCount > 0
                          ? `Apagar "${storage.name}" e os ${storage.itemCount} itens dentro dela?`
                          : `Apagar "${storage.name}"?`;
                      if (window.confirm(message)) {
                        void onChange(() => api.deleteStorage(storage.id), 'Dispensa apagada.');
                      }
                    }}
                  >
                    Apagar
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function PresetForm({ onSave }: { onSave: (category: string, days: number) => void }) {
  const [category, setCategory] = useState('');
  const [days, setDays] = useState(30);

  return (
    <form
      className="row"
      style={{ alignItems: 'flex-end' }}
      onSubmit={(event) => {
        event.preventDefault();
        if (!category.trim() || days <= 0) return;
        onSave(category.trim(), days);
        setCategory('');
      }}
    >
      <div className="field" style={{ marginBottom: 0, flex: '2 1 200px' }}>
        <label htmlFor="preset-category">Categoria</label>
        <input
          id="preset-category"
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          placeholder="Ex.: Leite aberto"
        />
      </div>
      <div className="field" style={{ marginBottom: 0, maxWidth: 120 }}>
        <label htmlFor="preset-days">Dias</label>
        <input
          id="preset-days"
          type="number"
          min={1}
          value={days}
          onChange={(event) => setDays(Number(event.target.value))}
        />
      </div>
      <button type="submit" className="btn" style={{ flex: '0 0 auto' }}>
        Adicionar
      </button>
    </form>
  );
}
